import { Deferred } from '../../../shared/utils/deferred/index.js';
import { ERuntimeStatus, type IRuntimeLoadable } from '../../runtime/interfaces.js';

import type { IRuntimeManifest } from '../../../shared/manifest/index.js';
import type { RuntimeBuilderFactory } from '../../bundles/interfaces.js';
import type { IImageLoadable } from '../image/interfaces.js';

export class RuntimeLoadable<RuntimeBuilder> implements IRuntimeLoadable, IRuntimeManifest {
  private readonly manifest: IRuntimeManifest;
  private _status: ERuntimeStatus = ERuntimeStatus.Initial;

  private readonly image: IImageLoadable;
  private loadDeferred: Deferred<this>;
  private setupFn: RuntimeBuilderFactory<RuntimeBuilder>;
  private _runtimeBuilder: RuntimeBuilder;

  public constructor(manifest: IRuntimeManifest, image: IImageLoadable) {
    this.manifest = manifest;

    this.image = image;
  }

  public get status() {
    return this._status;
  }

  public get type() {
    return this.manifest.type;
  }

  public get name() {
    return this.manifest.name;
  }

  public get bundle() {
    return this.manifest.bundle;
  }

  public get runtimeBuilder(): RuntimeBuilder {
    return this._runtimeBuilder;
  }

  public async ensure() {
    if (this._status === ERuntimeStatus.Ready) {
      return this;
    }

    if (this._status === ERuntimeStatus.BundleLoading) {
      return this.loadDeferred;
    }

    if (this._status === ERuntimeStatus.BuilderLoading) {
      return this.loadDeferred;
    }

    if (
      this._status === ERuntimeStatus.Initial ||
      this._status === ERuntimeStatus.BundleLoadFailed ||
      this._status === ERuntimeStatus.BuilderFailed
    ) {
      this.loadDeferred = new Deferred();
      this._status = ERuntimeStatus.BundleLoading;

      try {
        this.setupFn = (await this.image.load(this)) as RuntimeBuilderFactory<RuntimeBuilder>;

        this._status = ERuntimeStatus.BundleReady;
      } catch (error) {
        this._status = ERuntimeStatus.BundleLoadFailed;

        throw error;
      }
    }

    this._status = ERuntimeStatus.BuilderLoading;

    try {
      await this.loadBuilder();

      this.afterBuilderLoaded();
    } catch (error) {
      this._status = ERuntimeStatus.BuilderFailed;

      throw error;
    }

    this._status = ERuntimeStatus.Ready;

    this.loadDeferred.resolve(this);

    return this;
  }

  private async loadBuilder() {
    const promise = this.setupFn();

    if (promise instanceof Promise) {
      this._runtimeBuilder = await promise;
    } else {
      this._runtimeBuilder = promise;
    }
  }

  protected afterBuilderLoaded() {}
}
